import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  AudioLines,
  CircleDot,
  Command,
  GitBranch,
  Layers,
  Loader2,
  Plus,
  Search,
  Settings2,
  Sparkles,
  Sun,
  Upload,
  Waypoints,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ApiError, api, useWorkspace } from "./api";
import {
  ConstraintCards,
  ConstraintEditor,
  TaskEditor,
  StreamEditor,
  ParkTask,
} from "./WorkControls";
import { HistoryView } from "./HistoryView";
import { Onboarding } from "./Onboarding";
import { PEOPLE, blockers } from "../shared/schema";
import type { Constraint, Person, Task, Workstream, WorkEvent } from "../shared/schema";
import {
  Avatar,
  Empty,
  EventItem,
  Handoff,
  ImportModal,
  Login,
  MeetingDetail,
  Meetings,
  Modal,
  PersonCard,
  Settings,
  StreamRow,
  TaskDetail,
  UpdateModal,
  Workstreams,
  dateLabel,
  statusLabel,
} from "./components";

type View = "now" | "workstreams" | "handoffs" | "meetings" | "history";
type Panel =
  | { kind: "task"; id: string }
  | { kind: "meeting"; id: string; segment?: number; revision?: string }
  | null;
const navigation: { id: View; label: string; icon: LucideIcon }[] = [
  { id: "now", label: "Now", icon: Sun },
  { id: "workstreams", label: "Workstreams", icon: Layers },
  { id: "handoffs", label: "Handoffs", icon: GitBranch },
  { id: "meetings", label: "Meetings", icon: AudioLines },
  { id: "history", label: "History", icon: CircleDot },
];
function initialView(): View {
  const value = new URLSearchParams(location.search).get("view");
  return navigation.some((n) => n.id === value) ? (value as View) : "now";
}

export function App() {
  const query = useWorkspace();
  const client = useQueryClient();
  useEffect(() => {
    if (query.data) void client.invalidateQueries({ queryKey: ["history"] });
  }, [query.data?.historyCount, client]);
  const [view, setView] = useState<View>(initialView);
  const [streamFilter, setStreamFilter] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [updateOpen, setUpdateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mine, setMine] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [constraintEditor, setConstraintEditor] = useState<Constraint | null | undefined>(
    undefined,
  );
  const [taskEditor, setTaskEditor] = useState<Task | null | undefined>(undefined);
  const [streamEditor, setStreamEditor] = useState<Workstream | null | undefined>(undefined);
  const [parkTask, setParkTask] = useState<Task | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const sourceSearch = useQuery({
    queryKey: ["source-search", searchTerm],
    queryFn: () =>
      api<{
        passages: {
          meetingId: string;
          segment: number;
          text: string;
          speaker: string;
          title: string;
        }[];
      }>(`/search?q=${encodeURIComponent(searchTerm)}`),
    enabled: searchOpen && searchTerm.length >= 2,
  });
  const [temporaryPassword, setTemporaryPassword] = useState("");
  useEffect(() => {
    if (query.data) setTemporaryPassword("");
  }, [query.data]);
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    const listener = (): void => {
      setView(initialView());
      setStreamFilter(null);
    };
    window.addEventListener("popstate", listener);
    return () => window.removeEventListener("popstate", listener);
  }, []);
  function navigate(next: View, stream: string | null = null): void {
    setView(next);
    setStreamFilter(stream);
    history.pushState({}, "", `?view=${next}`);
  }
  async function mutate(path: string, body?: unknown, method = "POST"): Promise<boolean> {
    setPending(true);
    try {
      await api(path, { method, ...(body ? { body: JSON.stringify(body) } : {}) });
      await client.invalidateQueries({ queryKey: ["workspace"] });
      await client.invalidateQueries({ queryKey: ["meeting"] });
      await client.invalidateQueries({ queryKey: ["history"] });
      return true;
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 409)
        await client.invalidateQueries({ queryKey: ["workspace"] });
      setToast(error instanceof Error ? error.message : "That update could not be saved.");
      return false;
    } finally {
      setPending(false);
    }
  }
  async function patchTask(task: Task, patch: Record<string, unknown>): Promise<void> {
    if (await mutate(`/tasks/${task.id}`, { version: task.version, ...patch }, "PATCH"))
      setToast("Saved. Your plan is up to date.");
  }
  const openTask = (id: string): void => setPanel({ kind: "task", id });
  if (query.error instanceof ApiError && query.error.status === 401)
    return (
      <Login
        onSuccess={(password) => {
          setTemporaryPassword(password);
          void client.invalidateQueries({ queryKey: ["workspace"] });
        }}
      />
    );
  if (query.error instanceof ApiError && query.error.status === 428)
    return (
      <Onboarding
        temporaryPassword={temporaryPassword}
        onComplete={() => {
          setTemporaryPassword("");
          void client.invalidateQueries({ queryKey: ["workspace"] });
        }}
      />
    );
  if (query.error instanceof ApiError && query.error.status === 403)
    return (
      <div className="full-state">
        <h1>This account needs attention.</h1>
        <p>{query.error.message}</p>
        <button
          className="button primary"
          onClick={() => {
            void api("/auth/sign-out", { method: "POST", body: "{}" }).finally(() =>
              location.reload(),
            );
          }}
        >
          Return to sign in
        </button>
      </div>
    );
  if (!query.data)
    return (
      <div className="full-state">
        <div className="brand-mark">
          <Waypoints size={24} />
        </div>
        <h1>{query.error ? "Let’s reconnect your workspace." : "Opening your workroom."}</h1>
        <p>
          {query.error
            ? query.error.message
            : "Finding the thread between your conversations and your work."}
        </p>
        {query.error ? (
          <button
            className="button primary"
            onClick={() => {
              void query.refetch();
            }}
          >
            Try again
          </button>
        ) : (
          <Loader2 className="spin" size={23} />
        )}
      </div>
    );
  const data = query.data;
  const handoffs = data.tasks.filter(
    (t) =>
      !["done", "parked"].includes(t.status) &&
      data.streams.find((s) => s.id === t.streamId)?.state === "active" &&
      (blockers(t, data.tasks).length > 0 || t.waitingFor || t.status === "waiting"),
  );
  const meaningfulEvents = data.events.filter((e) => e.kind !== "import");
  const activeStreams = data.streams.filter((s) => s.state === "active");
  const syncFresh =
    !!data.integration.lastSeenAt && Date.now() - Date.parse(data.integration.lastSeenAt) < 180000;
  function openEvent(event: WorkEvent): void {
    const evidence = event.evidence?.[0];
    if (evidence)
      setPanel({
        kind: "meeting",
        id: evidence.meetingId,
        segment: evidence.segment,
        revision: evidence.revision,
      });
    else if (event.taskId) openTask(event.taskId);
    else if (event.meetingId) setPanel({ kind: "meeting", id: event.meetingId });
  }

  const selectedTask =
    panel?.kind === "task" ? data.tasks.find((t) => t.id === panel.id) : undefined;
  const people: Person[] = mine ? [data.viewer] : ["charlton", "david"];
  const currentLabel = navigation.find((n) => n.id === view)?.label ?? "Now";
  const pendingMeetings = data.meetings.filter((m) => ["queued", "processing"].includes(m.state));
  const matches = data.tasks.filter((t) =>
    `${t.title} ${t.brief} ${PEOPLE[t.owner].name}`.toLowerCase().includes(search.toLowerCase()),
  );
  const meetingMatches = data.meetings
    .filter((m) => `${m.title} ${m.summary}`.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 8);
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <a
          className="brand"
          href="/?view=now"
          onClick={(e) => {
            e.preventDefault();
            navigate("now");
          }}
        >
          <span className="brand-mark">
            <Waypoints size={23} />
          </span>
          workroom<span className="brand-dot">.</span>
        </a>
        <div className="workspace-label">
          CHARLTON & DAVID <span>↗</span>
        </div>
        <nav aria-label="Workspace">
          {navigation.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              aria-label={label}
              aria-current={view === id ? "page" : undefined}
              className={`nav-item ${view === id ? "active" : ""}`}
              onClick={() => navigate(id)}
            >
              <Icon size={18} />
              {label}
              {id === "handoffs" && handoffs.length > 0 && (
                <span className="nav-count">{handoffs.length}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="sidebar-section">YOUR WORKSTREAMS</div>
        {activeStreams.map((s) => (
          <button className="stream-nav" key={s.id} onClick={() => navigate("workstreams", s.id)}>
            <span className={`stream-dot tone-${s.tone}`} />
            {s.name}
          </button>
        ))}
        <div className="sidebar-bottom">
          <button className="capture-status" onClick={() => setSettingsOpen(true)}>
            <span className="status-light" />
            <span>
              {data.integration.failures
                ? "UltraMeet sync needs attention"
                : syncFresh
                  ? "UltraMeet sync connected"
                  : "UltraMeet archive"}
              <small>
                {data.meetings.filter((m) => m.appName === "Signal").length} Signal conversations ·{" "}
                {pendingMeetings.length
                  ? "processing"
                  : syncFresh
                    ? "checked recently"
                    : data.integration.lastSeenAt
                      ? `last checked ${dateLabel(data.integration.lastSeenAt, data.timezone)}`
                      : "sync status unavailable"}
              </small>
            </span>
          </button>
          <button
            className="nav-item"
            aria-label="Workspace settings"
            onClick={() => setSettingsOpen(true)}
          >
            <Settings2 size={17} />
            Workspace settings
          </button>
          <div className="profile">
            <Avatar person={data.viewer} />
            <div>
              {data.displayName ?? PEOPLE[data.viewer].name}
              <small>{data.localMode ? "Local workspace" : "Shared workspace"}</small>
            </div>
            <button
              className="profile-key"
              aria-label="Open search"
              onClick={() => setSearchOpen(true)}
            >
              ⌘ K
            </button>
          </div>
        </div>
      </aside>
      <main className="main-shell">
        <header className="topbar">
          <div>
            Workspace<span className="slash">/</span>
            <strong>{currentLabel}</strong>
          </div>
          <button className="search-trigger" onClick={() => setSearchOpen(true)}>
            <Search size={15} />
            Search your work<span>⌘ K</span>
          </button>
          <div className="team-avatars">
            <Avatar person="charlton" small />
            <Avatar person="david" small />
          </div>
        </header>
        <div className="page-content">
          <div className="page-heading">
            <div>
              <div className="eyebrow">
                {view === "now" ? "A SHARED SENSE OF DIRECTION" : currentLabel.toUpperCase()}
              </div>
              <h1>
                {
                  {
                    now: "Here's where we stand",
                    workstreams: "Keep the outcome in sight",
                    handoffs: "Move things forward, together",
                    meetings: "Keep the whole conversation",
                    history: "The thread of your work",
                  }[view]
                }
                <span>.</span>
              </h1>
              <p>
                {
                  {
                    now: "The important work. The next move. Both of you on the same page.",
                    workstreams: "One shared plan, organized around what you want to achieve.",
                    handoffs: "The inputs that release the next piece of work.",
                    meetings:
                      "Your conversations, decisions, and original context, all within reach.",
                    history: "What changed, why it changed, and where it came from.",
                  }[view]
                }
              </p>
            </div>
            <button
              className="button primary"
              onClick={() => (view === "meetings" ? setImportOpen(true) : setUpdateOpen(true))}
            >
              {view === "meetings" ? <Upload size={16} /> : <Plus size={16} />}
              {view === "meetings" ? "Import conversation" : "Add an update"}
            </button>
          </div>
          {query.error && (
            <div className="inline-alert">
              Connection interrupted. Showing your last saved plan.{" "}
              <button
                onClick={() => {
                  void query.refetch();
                }}
              >
                Reconnect
              </button>
            </div>
          )}
          {pendingMeetings.length > 0 && (
            <div className="processing-strip">
              <Loader2 size={14} className="spin" />
              {pendingMeetings.length} conversation{pendingMeetings.length === 1 ? "" : "s"}{" "}
              {data.aiAvailable
                ? "being reconciled with your plan"
                : "saved; waiting for local processing"}
              <button onClick={() => navigate("meetings")}>
                View progress <ArrowRight size={13} />
              </button>
            </div>
          )}
          {view === "now" && (
            <>
              <ConstraintCards
                data={data}
                onEdit={setConstraintEditor}
                onAdd={() => setConstraintEditor(null)}
              />
              <details className="planning-context">
                <summary>
                  <Sparkles size={15} />
                  <span>Last planning context</span>
                  <strong>{data.focus.title}</strong>
                </summary>
                <p>{data.focus.detail}</p>
                <small>
                  This is context from a planning conversation. The next actions below reflect the
                  current task state.
                </small>
                {data.focus.evidence && (
                  <button
                    className="source-pill"
                    onClick={() =>
                      setPanel({
                        kind: "meeting",
                        id: data.focus.evidence!.meetingId,
                        segment: data.focus.evidence!.segment,
                        revision: data.focus.evidence!.revision,
                      })
                    }
                  >
                    <AudioLines size={13} />
                    Open the source conversation <ArrowUpRight size={12} />
                  </button>
                )}
              </details>
              <div className="content-grid">
                <div>
                  <div className="section-heading">
                    <h2>Your next moves</h2>
                    <span className="segmented">
                      <button className={!mine ? "selected" : ""} onClick={() => setMine(false)}>
                        Together
                      </button>
                      <button className={mine ? "selected" : ""} onClick={() => setMine(true)}>
                        My focus
                      </button>
                    </span>
                  </div>
                  <div className={`people-grid ${mine ? "single" : ""}`}>
                    {people.map((person) => (
                      <PersonCard key={person} person={person} data={data} onOpen={openTask} />
                    ))}
                  </div>
                  <div className="section-heading streams-heading">
                    <h2>The bigger picture</h2>
                    <button className="text-button" onClick={() => navigate("workstreams")}>
                      All workstreams <ArrowUpRight size={14} />
                    </button>
                  </div>
                  <div className="streams-table">
                    {activeStreams.map((stream) => (
                      <StreamRow
                        key={stream.id}
                        stream={stream}
                        tasks={data.tasks}
                        onClick={() => navigate("workstreams", stream.id)}
                      />
                    ))}
                  </div>
                </div>
                <aside className="right-rail">
                  <div className="section-heading">
                    <h2>Between you</h2>
                    <button className="number-label" onClick={() => navigate("handoffs")}>
                      {String(handoffs.length).padStart(2, "0")}
                    </button>
                  </div>
                  <div className="rail-handoffs">
                    {handoffs.slice(0, 2).map((task) => (
                      <Handoff key={task.id} task={task} tasks={data.tasks} onOpen={openTask} />
                    ))}
                    {!handoffs.length && (
                      <div className="empty-small">No handoffs waiting. You’re clear to move.</div>
                    )}
                  </div>
                  <div className="rail-heading">
                    <h2>What changed</h2>
                    <span className="tiny-label">THE LATEST UPDATES</span>
                  </div>
                  <div className="timeline">
                    {meaningfulEvents.slice(0, 3).map((event) => (
                      <EventItem
                        timezone={data.timezone}
                        key={event.id}
                        event={event}
                        onOpen={() => {
                          if (event.taskId) openTask(event.taskId);
                          else if (event.meetingId)
                            setPanel({ kind: "meeting", id: event.meetingId });
                        }}
                      />
                    ))}
                  </div>
                  <div className="quiet-note">
                    <ArrowDownRight size={20} />
                    <p>
                      You talk it through.
                      <br />
                      Workroom keeps the thread.
                    </p>
                  </div>
                </aside>
              </div>
            </>
          )}
          {view === "workstreams" && (
            <div className="work-toolbar">
              <button className="button secondary" onClick={() => setTaskEditor(null)}>
                <Plus size={14} />
                Add task
              </button>
              <button className="button secondary" onClick={() => setStreamEditor(null)}>
                Add workstream
              </button>
              <button className="text-button" onClick={() => setConstraintEditor(null)}>
                Add constraint
              </button>
              {streamFilter && (
                <button
                  className="text-button"
                  onClick={() =>
                    setStreamEditor(data.streams.find((s) => s.id === streamFilter) ?? null)
                  }
                >
                  Edit this workstream
                </button>
              )}
            </div>
          )}
          {view === "workstreams" && (
            <Workstreams
              data={data}
              filter={streamFilter}
              onFilter={setStreamFilter}
              onTask={openTask}
            />
          )}
          {view === "handoffs" && (
            <>
              <div className="handoff-intro">
                <GitBranch size={22} />
                <p>
                  A handoff is a specific input someone needs. Finish the prerequisite and the next
                  action becomes available automatically.
                </p>
              </div>
              <div className="handoff-grid">
                {handoffs.map((task) => (
                  <Handoff key={task.id} task={task} tasks={data.tasks} onOpen={openTask} />
                ))}
                {!handoffs.length && (
                  <Empty
                    title="Nothing stuck between you"
                    text="When work needs another person’s input, it will appear here."
                  />
                )}
              </div>
            </>
          )}
          {view === "meetings" && (
            <Meetings
              timezone={data.timezone}
              meetings={data.meetings}
              onOpen={(id) => setPanel({ kind: "meeting", id })}
            />
          )}
          {view === "history" && (
            <HistoryView
              timezone={data.timezone}
              onOpen={openEvent}
              onUndo={(id) => {
                void mutate(`/events/${id}/undo`);
              }}
              pending={pending}
            />
          )}
          <footer className="page-footer">
            <span>
              <span className="status-light" />
              {pending ? "Saving…" : "Saved to your shared workspace"}
            </span>
            <span>
              <Command size={12} /> K to find anything
            </span>
          </footer>
        </div>
      </main>
      <Dialog.Root
        open={!!panel}
        onOpenChange={(open) => {
          if (!open) setPanel(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="drawer-overlay" />
          <Dialog.Content className="detail-drawer">
            <div className="drawer-top">
              <span className="eyebrow">
                {panel?.kind === "meeting" ? "THE ORIGINAL CONTEXT" : "A CLEAR NEXT MOVE"}
              </span>
              <Dialog.Close className="icon-button" aria-label="Close details">
                <X size={19} />
              </Dialog.Close>
            </div>
            <Dialog.Title className="sr-only">
              {selectedTask?.title ?? "Conversation details"}
            </Dialog.Title>
            <Dialog.Description className="sr-only">
              Work details and the original evidence behind them.
            </Dialog.Description>
            {selectedTask && (
              <TaskDetail
                task={selectedTask}
                data={data}
                pending={pending}
                onPatch={patchTask}
                onEvidence={(e) =>
                  setPanel({
                    kind: "meeting",
                    id: e.meetingId,
                    segment: e.segment,
                    revision: e.revision,
                  })
                }
                onTask={openTask}
                onEdit={() => {
                  setTaskEditor(selectedTask);
                  setPanel(null);
                }}
                onPark={() => {
                  setParkTask(selectedTask);
                  setPanel(null);
                }}
                onRelease={() => {
                  void mutate(`/tasks/${selectedTask.id}/release-correction`);
                }}
              />
            )}
            {panel?.kind === "meeting" && (
              <MeetingDetail
                timezone={data.timezone}
                id={panel.id}
                segment={panel.segment}
                revision={panel.revision}
                pending={pending}
                onRetry={() => {
                  void mutate(`/meetings/${panel.id}/retry`);
                }}
              />
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <Modal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Find the thread"
        description="Search your work, owners, and meeting summaries."
        wide
      >
        <div className="search-box">
          <Search size={18} />
          <input
            autoFocus
            aria-label="Search work and meetings"
            placeholder="A project, a decision, a person…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="search-results">
          <div className="eyebrow">WORK</div>
          {matches.slice(0, 10).map((task) => (
            <button
              className="search-result"
              key={task.id}
              onClick={() => {
                setSearchOpen(false);
                openTask(task.id);
              }}
            >
              <CircleDot size={15} />
              <span>
                {task.title}
                <small>
                  {PEOPLE[task.owner].name} · {statusLabel(task, data.tasks)}
                </small>
              </span>
              <ArrowUpRight size={14} />
            </button>
          ))}
          {!matches.length && <p className="empty-small">No matching work.</p>}
          {searchTerm.length >= 2 && (
            <>
              <div className="eyebrow">IN THE ORIGINAL CONVERSATIONS</div>
              {sourceSearch.isFetching && <p className="form-note">Searching source passages…</p>}
              {sourceSearch.error && <p className="inline-alert">{sourceSearch.error.message}</p>}
              {sourceSearch.data?.passages.map((p) => (
                <button
                  className="search-result source-result"
                  key={`${p.meetingId}-${p.segment}`}
                  onClick={() => {
                    setSearchOpen(false);
                    setPanel({ kind: "meeting", id: p.meetingId, segment: p.segment });
                  }}
                >
                  <AudioLines size={15} />
                  <span>
                    {p.title}
                    <small>
                      {p.speaker}: {p.text.slice(0, 240)}
                      {p.text.length > 240 ? "…" : ""}
                    </small>
                  </span>
                </button>
              ))}
            </>
          )}
          <div className="eyebrow">CONVERSATIONS</div>
          {meetingMatches.map((meeting) => (
            <button
              className="search-result"
              key={meeting.id}
              onClick={() => {
                setSearchOpen(false);
                setPanel({ kind: "meeting", id: meeting.id });
              }}
            >
              <AudioLines size={15} />
              <span>
                {meeting.title}
                <small>
                  {dateLabel(meeting.occurredAt, data.timezone)} · {meeting.segmentCount} passages
                </small>
              </span>
              <ArrowUpRight size={14} />
            </button>
          ))}
        </div>
      </Modal>
      <UpdateModal
        open={updateOpen}
        onClose={() => setUpdateOpen(false)}
        viewer={data.viewer}
        localMode={data.localMode}
        aiAvailable={data.aiAvailable}
        pending={pending}
        onSave={async (value) => {
          const ok = await mutate("/updates", value);
          if (ok) {
            setToast(
              data.aiAvailable
                ? "Update saved. Workroom is reconciling the plan."
                : "Update saved. Waiting for local processing.",
            );
            setUpdateOpen(false);
          }
          return ok;
        }}
      />
      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        pending={pending}
        onSave={async (value) => {
          if (await mutate("/meetings", value)) {
            setImportOpen(false);
            setToast("Conversation saved. The shared plan will update after analysis.");
          }
        }}
      />
      {constraintEditor !== undefined && (
        <ConstraintEditor
          key={constraintEditor?.id ?? "new-constraint"}
          value={constraintEditor}
          data={data}
          pending={pending}
          onClose={() => setConstraintEditor(undefined)}
          onSave={(body) =>
            mutate(
              constraintEditor ? `/constraints/${constraintEditor.id}` : "/constraints",
              body,
              constraintEditor ? "PATCH" : "POST",
            )
          }
        />
      )}
      {taskEditor !== undefined && (
        <TaskEditor
          key={taskEditor?.id ?? "new-task"}
          task={taskEditor}
          data={data}
          pending={pending}
          onClose={() => setTaskEditor(undefined)}
          onSave={(body) =>
            mutate(
              taskEditor ? `/tasks/${taskEditor.id}` : "/tasks",
              body,
              taskEditor ? "PATCH" : "POST",
            )
          }
        />
      )}
      {streamEditor !== undefined && (
        <StreamEditor
          key={streamEditor?.id ?? "new-stream"}
          stream={streamEditor}
          pending={pending}
          onClose={() => setStreamEditor(undefined)}
          onSave={(body) =>
            mutate(
              streamEditor ? `/workstreams/${streamEditor.id}` : "/workstreams",
              body,
              streamEditor ? "PATCH" : "POST",
            )
          }
        />
      )}
      {parkTask && (
        <ParkTask
          task={parkTask}
          pending={pending}
          onClose={() => setParkTask(null)}
          onSave={(body) => mutate(`/tasks/${parkTask.id}`, body, "PATCH")}
        />
      )}
      <Settings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      {createPortal(
        <div aria-live="polite" aria-atomic="true" role={toast ? "status" : undefined}>
          {toast && (
            <div className="toast">
              <span>{toast}</span>
              <button aria-label="Dismiss notification" onClick={() => setToast(null)}>
                <X size={15} />
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
